/* @flow */
import React from 'react';

import ChatSettingsActions from '../actions/ChatSettingsActions';
import ClassCardSection from 'settings/components/ClassCardSection.jsx';
import Divider from 'components/Divider.jsx';
import FeatureStore from 'stores/FeatureStore';
import classesDefaultsFragment from 'groups/fragments/classesDefaultsFragment.gql';
import Group from 'entities/Group';
import GroupCreateModal from 'groups/components/GroupCreateModal.jsx';
import JoinGroupModal from 'groups/components/JoinGroupModal.jsx';
import LoadingIndicator from 'components/LoadingIndicator.jsx';
import minimalRelatedUserFragment from 'user/fragments/minimalRelatedUserFragment.gql';
import OfficeHoursPicker from 'settings/components/OfficeHoursPicker.jsx';
import ProfileHeaderView from './ProfileHeaderView.jsx';
import ToastActions from 'actions/ToastActions';
import UserParentChildSection from './UserParentChildSection.jsx';
import UserSchoolsForm from './UserSchoolsForm.jsx';
import { graphql } from 'react-apollo';
import gql from 'graphql-tag';

import './styles/UserProfileView.scss';

type Props = {
  loading?: ?boolean,
  error?: Object,
  router: Object,
  ownedClasses: Group[],
  subscribedClasses: Group[],
  family: Object,
  permissions: Object,
  organizationPickerEligible: boolean,
  role: ?string,
};

class UserProfileView extends React.Component {
  props: Props;

  static onEnter(nextState, replaceWith) {
    if (!FeatureStore.hasFeature('web.new_account')) {
      replaceWith('/settings');
    }
  }

  componentDidMount() {
    ChatSettingsActions.loadOfficeHours();
  }

  // XXX until we have a good mechanism for GraphQL errors (e.g. re-auth)
  // https://apollographql.slack.com/archives/general/p1478732615013498
  // FIXME(turadg)
  componentWillReceiveProps(nextProps: Object) {
    if (!this.props.error && nextProps.error) {
      ToastActions.showDefaultErrorToast();
      console.warn(nextProps);
      console.error('GraphQL error');
    }
  }

  onCompleteGroup = (group) => {
    this.props.router.push(group.webUrl());
  }

  renderParentsSection() {
    if (this.props.family.parents.length ||
        this.props.family.pending_parents.length ||
        this.props.permissions.can_add_parent) {
      return (
        <div className="UserProfileView-section">
          <UserParentChildSection relation="parent" />
          <Divider size="large" />
        </div>
      );
    } else {
      return null;
    }
  }

  renderChildrenSection() {
    if (this.props.family.children.length ||
        this.props.family.pending_children.length ||
        this.props.permissions.can_add_child) {
      return (
        <div className="UserProfileView-section">
          <UserParentChildSection relation="child" />
          <Divider size="large" />
        </div>
      );
    } else {
      return null;
    }
  }

  renderSchoolsSection() {
    if (!this.props.organizationPickerEligible) {
      return null;
    }

    return (
      <div className="UserProfileView-section">
        <UserSchoolsForm />
        <Divider size="large" />
      </div>
    );
  }

  renderOfficeHoursSection() {
    if (this.props.role !== 'teacher') {
      return null;
    }

    return (
      <div className="UserProfileView-section">
        <OfficeHoursPicker showToastMessages showToggleButton showIntroText compact />
        <Divider size="large" />
      </div>
    );
  }

  renderOwnedClassSection() {
    const { ownedClasses, permissions } = this.props;
    if (!permissions.can_own_groups || ownedClasses.length === 0) {
      return null;
    }

    return (
      <div className="UserProfileView-section">
        <ClassCardSection
          label="Classes owned"
          items={ownedClasses}
          addModal={<GroupCreateModal onCreate={this.onCompleteGroup} />}
          addText="Create a class"
          addEnabled={permissions.can_create_group}
        />
        <Divider size="large" />
      </div>
    );
  }

  renderJoinedClassSection() {
    const { subscribedClasses } = this.props;
    if (subscribedClasses.length === 0) {
      return null;
    }
    return (
      <div className="UserProfileView-section">
        <ClassCardSection
          label="Classes joined"
          items={subscribedClasses}
          addModal={<JoinGroupModal onJoin={this.onCompleteGroup} />}
          addText="Join a class"
        />
      </div>
    );
  }

  render() {
    if (this.props.loading || this.props.error) {
      return <LoadingIndicator />;
    }

    const showFamily = FeatureStore.hasFeature('web.profile.family');

    return (
      <div className="UserProfileView">
        <ProfileHeaderView />
        {showFamily && this.renderParentsSection()}
        {showFamily && this.renderChildrenSection()}
        {this.renderSchoolsSection()}
        {this.renderOfficeHoursSection()}
        {this.renderOwnedClassSection()}
        {this.renderJoinedClassSection()}
      </div>
    );
  }
}

const withData = graphql(gql`
  query {
    me {
      id
      # TODO https://app.asana.com/0/52068106376728/221203365474362
      organization_picker_eligible
      permissions {
        can_add_parent
        can_add_child
        can_create_group
        can_own_groups
      }
      role
      ownedClasses: classes(classType: "owned") {
        ...classesDefaults
      }
      subscribedClasses: classes(classType: "subscribed") {
        ...classesDefaults
      }
      family {
        children {
          ...minimalRelatedUser
        }
        pending_children {
          ...minimalRelatedUser
        }
        parents {
          ...minimalRelatedUser
        }
        pending_parents {
          ...minimalRelatedUser
        }
      }
    }
  }
  ${minimalRelatedUserFragment}
  ${classesDefaultsFragment}
  `, {
    // TODO decide whether to make this the default `props` function
    props: ({ data }) => {
      const defaultMe = {
        ownedClasses: [],
        subscribedClasses: [],
        family: {},
        permissions: {},
        organization_picker_eligible: false,
        role: null,
      };

      const me = data.me || defaultMe;

      const ownedClasses = me.ownedClasses.map(c => Group.fromJSON(c));
      const subscribedClasses = me.subscribedClasses.map(c => Group.fromJSON(c));
      const organizationPickerEligible = me.organization_picker_eligible;
      const { family, permissions, role } = me;

      return Object.assign({}, data, {
        ownedClasses,
        subscribedClasses,
        family,
        permissions,
        organizationPickerEligible,
        role,
      });
    },
  }
);


export default withData(UserProfileView);



// WEBPACK FOOTER //
// ./src/settings/components/UserProfileView.jsx